import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchApi } from '../../../helpers/fetch';
import { AnalyticsSubtabListSkeleton } from '../../components/Skeletons/BatchScreenSkeletons';
import Icon from '../../components/Icon';
import './NewsAnalytics.scss';

function NewsAnalytics({ userId }) {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchNews = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await fetchApi(`/api/news/${userId}`);
      const list = response?.success && Array.isArray(response?.data) ? response.data : [];
      setNews(
        list.map((n) => ({
          ...n,
          views: n.views ?? n.view_count ?? 0,
          likes: Array.isArray(n.likes) ? n.likes.length : (n.total_likes ?? 0),
        }))
      );
    } catch {
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchNews();
    }
  }, [userId, fetchNews]);

  const filteredNews = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return news;
    return news.filter((n) => (n.title || '').toLowerCase().includes(q));
  }, [news, search]);

  const handleNewsClick = (item) => {
    navigate(`/creator-analytics/news/${item.id}`);
  };

  const handleNewsKeyDown = (e, item) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNewsClick(item);
    }
  };

  const handleFilterClick = () => {
    // Filter modal/panel placeholder – can be expanded later
  };

  return (
    <div
      id="panel-news"
      role="tabpanel"
      aria-labelledby="tab-news"
      className="news-analytics"
    >
      <div className="news-analytics__toolbar">
        <input
          type="search"
          className="news-analytics__search"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search news"
        />
        {/* <button
          type="button"
          className="news-analytics__filter-btn"
          onClick={handleFilterClick}
          aria-label="Filter news"
        >
          <Icon alias="filter" width={20} height={20} color="#FF3232" />
        </button> */}
      </div>

      {loading ? (
        <AnalyticsSubtabListSkeleton variant="news" />
      ) : filteredNews.length === 0 ? (
        <div className="news-analytics__empty">
          <p>No news found</p>
        </div>
      ) : (
        <ul className="news-analytics__list" role="list">
          {filteredNews.map((item) => (
            <li key={item.id} className="news-analytics__item" role="listitem">
              <div
                className="news-analytics__card"
                onClick={() => handleNewsClick(item)}
                onKeyDown={(e) => handleNewsKeyDown(e, item)}
                role="button"
                tabIndex={0}
                aria-label={`View ${item.title || 'news'}`}
              >
                <div className="news-analytics__thumb">
                  {item.media_url ? (
                    <img src={item.media_url} alt={item.title || ''} />
                  ) : (
                    <div className="news-analytics__placeholder" />
                  )}
                </div>
                <div className="news-analytics__info">
                  <h3 className="news-analytics__title">
                    {item.title || 'Untitled'}
                  </h3>
                  <div className="news-analytics__meta">
                    <span className="news-analytics__views">
                      <Icon
                        alias="eye_views"
                        width={14}
                        height={14}
                        color="currentColor"
                      />
                      {typeof item.views === 'number'
                        ? item.views.toLocaleString()
                        : (item.view_count ?? 0).toLocaleString()}
                    </span>
                    <span className="news-analytics__likes">
                      <Icon
                        alias="heart_likes"
                        width={14}
                        height={14}
                        color="currentColor"
                      />
                      {(typeof item.likes === 'number' ? item.likes : 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default NewsAnalytics;
